type EntityStatusIconProps = {
  status: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
};

const sizeMap = {
  xs: 'h-3 w-3',
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-[18px] w-[18px]',
} as const;

function BacklogIcon() {
  return <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="4 3" />;
}

function TodoIcon() {
  return <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />;
}

function AssignedIcon() {
  return (
    <>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M12 3 A9 9 0 0 1 21 12 L12 12 Z" fill="currentColor" />
    </>
  );
}

function InProgressIcon() {
  return (
    <>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M12 3 A9 9 0 0 1 12 21 L12 12 Z" fill="currentColor" />
    </>
  );
}

function DoneIcon() {
  return (
    <>
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <path
        d="M8 12.5 L10.5 15 L16 9.5"
        fill="none"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  );
}

function BlockedIcon() {
  return (
    <>
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <line x1="12" y1="7" x2="12" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="16.5" r="1.2" fill="white" />
    </>
  );
}

function CancelledIcon() {
  return (
    <>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
      <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </>
  );
}

// 건너뜀(작업 전용 종료 상태): 채운 원 + 흰 이중 chevron(»). "지나감"을 형상화하며 DONE/BLOCKED와 구분된다.
function SkippedIcon() {
  return (
    <>
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <path
        d="M7.5 8.5 L11 12 L7.5 15.5"
        fill="none"
        stroke="white"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 8.5 L15.5 12 L12 15.5"
        fill="none"
        stroke="white"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  );
}

const statusIconMap: Record<string, () => React.JSX.Element> = {
  BACKLOG: BacklogIcon,
  TODO: TodoIcon,
  ASSIGNED: AssignedIcon,
  IN_PROGRESS: InProgressIcon,
  // PARTIAL은 전용 글리프가 없어 ASSIGNED 글리프를 재사용한다.
  PARTIAL: AssignedIcon,
  DONE: DoneIcon,
  SKIPPED: SkippedIcon,
  BLOCKED: BlockedIcon,
  CANCELLED: CancelledIcon,
};

/**
 * 진행 단계(lifecycle)를 형상으로 인코딩하는 공통 상태 글리프.
 * `status`는 글리프 키(BACKLOG/TODO/ASSIGNED/IN_PROGRESS/DONE/SKIPPED/BLOCKED/CANCELLED)이며 entityType-무관이다.
 * 색은 `className`으로 주입한다(STATUS_ICON_REGISTRY의 color). 알 수 없는 키는 렌더하지 않는다.
 */
export function EntityStatusIcon({ status, size = 'md', className = '' }: EntityStatusIconProps) {
  const IconContent = statusIconMap[status];
  if (!IconContent) return null;

  const isSpinning = status === 'IN_PROGRESS';

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      className={`${sizeMap[size]} shrink-0 align-middle ${isSpinning ? 'animate-spin' : ''} ${className}`}
      aria-hidden
    >
      <IconContent />
    </svg>
  );
}
